/**
 * Splits a bundle card's product title into a name and a brand/colour caption.
 *
 *   "Arthur Beale Basic Sweat Shorts - Light Pink"
 *     ->  Basic Sweat Shorts
 *         ARTHUR BEALE | LIGHT PINK
 *
 * WHY THIS IS SAFE TO DERIVE. Every managed product's title is "<vendor> <name> - <colour>"
 * and carries a populated vendor: measured across all 111 managed products on 2026-08-14,
 * 111 had exactly one " - ", 111 had a vendor, and the vendor was a literal prefix of the
 * title in all 111. The name ends with the product type in all 108 that are cards. Nothing
 * here guesses where a brand ends — "Coney Island Picnic" and "BLVD" both come from the
 * vendor field rather than from counting words.
 *
 * WHY IT RUNS ON A MutationObserver. The Easy Bundles app owns this markup and re-renders
 * its cards, so a one-shot pass at load is undone the first time it repaints — the same
 * reason assets/tb-one-per-variant.js observes rather than running once.
 *
 *
 * DO NOT POLL THE DEPLOYED ASSET URL WHILE A DEPLOY IS LANDING. Shopify serves theme assets
 * with `cache-control: max-age=31557600` — one year — and Cloudflare caches the body under
 * the `?v=` key the page happens to be rendering. Fetching that URL during the window
 * between "the version rolled" and "the origin serves the new body" caches the OLD body
 * under the NEW key, for a year, and no query string or no-cache header dislodges it
 * (verified: `cf-cache-status: HIT`, `age: 33673`, identical bytes for `?v=...&bust=random`).
 * The only way out is another byte change, which mints a fresh key. Deploy, wait, then fetch
 * ONCE.
 * FAILURE IS ALWAYS THE PLAIN TITLE. Anything that does not parse, and any product the app
 * data does not name, is left exactly as the app rendered it. If Easy Bundles restructures
 * its page data, every card quietly reverts to the untouched title rather than breaking.
 */

/** Product types whose Odoo spelling is not what a shopper should read. Keyed lower-case. */
export const TYPE_LABELS = {
  "ss tees": "T-Shirt",
};

/**
 * Subcategory chip labels, keyed by the chip's own data-tb-value.
 *
 * Separate from TYPE_LABELS on purpose: a chip names a category and a card names one
 * product, so the same type wants a plural on the chip and a singular on the card —
 * "T-Shirts" over the grid, "Camaro Checkered T-Shirt" on the item.
 *
 * Renaming the visible text is safe because subcategory-filter.js keys its filter on
 * data-tb-value, never on the label — verified in the rendered markup, where the All chip
 * carries data-tb-value="__tbAll__" and its own text quite separately.
 */
export const CHIP_LABELS = {
  __tbAll__: "View All",
  "SS tees": "T-Shirts",
  "Swim Shorts": "Swim",
};

/**
 * Vendors that are not brands. "Licensed" is Odoo's licensing category, carried on 8 of the
 * 111 managed products (the Camaro, Iron Maiden and Nintendo tees), and "LICENSED | RED"
 * reads worse than the colour alone. The caption drops to just the colour for these.
 */
export const BRANDLESS_VENDORS = new Set(["licensed"]);

const NAME_CLASS = "tbCardName";
const META_CLASS = "tbCardMeta";
const DONE_ATTR = "tbTitleSplit";
const PRICE_ATTR = "tbPriceTrimmed";
/** Styled in tb-custom.css; the badge reads this to say "already in your box". */
const IN_BOX_CLASS = "tbInBox";

/**
 * The name and caption for one product, or null when the title does not have the shape
 * this depends on — in which case the caller leaves the rendered title alone.
 */
export function splitTitle(title, vendor, type) {
  if (typeof title !== "string" || typeof vendor !== "string") return null;
  const trimmedTitle = title.trim();
  const trimmedVendor = vendor.trim();
  if (!trimmedTitle || !trimmedVendor) return null;

  const dash = trimmedTitle.lastIndexOf(" - ");
  if (dash < 0) return null;
  const colour = trimmedTitle.slice(dash + 3).trim();

  let name = trimmedTitle.slice(0, dash).trim();
  if (!name.toLowerCase().startsWith(trimmedVendor.toLowerCase())) return null;
  name = name.slice(trimmedVendor.length).trim();
  if (!name || !colour) return null;

  name = relabelType(name, type);

  const brand = BRANDLESS_VENDORS.has(trimmedVendor.toLowerCase())
    ? null
    : trimmedVendor;
  return { name, brand, colour, meta: brand ? `${brand} | ${colour}` : colour };
}

/**
 * The name with a trailing product type swapped for its shopper-facing label. The type is
 * matched only at the end, which is where it sits in every managed title, so a colour or a
 * design name that happens to contain the same words is never rewritten.
 */
export function relabelType(name, type) {
  if (typeof type !== "string" || !type.trim()) return name;
  const label = TYPE_LABELS[type.trim().toLowerCase()];
  if (!label) return name;
  const tail = type.trim().toLowerCase();
  if (!name.toLowerCase().endsWith(tail)) return name;
  const head = name.slice(0, name.length - tail.length).trim();
  return head ? `${head} ${label}` : label;
}

/**
 * A money string with its trailing ".00" removed, and anything else untouched.
 *
 * Safe because every managed price is a whole dollar: measured across all 486 variants on
 * 2026-08-14, zero ended in anything but .00, and the catalog job's rule prices at 100% of
 * MSRP rather than at .99 points. The match is anchored at both ends so a real cents value
 * is never truncated — "$25.50" must not render "$25", which would understate a price.
 */
export function dropWholeCents(text) {
  if (typeof text !== "string") return text;
  const match = text.match(/^(\s*\D*\d[\d,]*)\.00(\D*\s*)$/);
  return match ? `${match[1]}${match[2]}` : text;
}

/**
 * id -> { vendor, type } for everything the app has loaded, found by walking its page data
 * for arrays of product-shaped objects rather than by the path they sit at today
 * (_bundleData.productsData1.categories.category16833.products — the step and category keys
 * are generated, so pinning the path would break on the next bundle).
 */
export function productIndex(root) {
  const index = new Map();
  const seen = new Set();
  const walk = (node, depth) => {
    if (!node || typeof node !== "object" || depth > 8 || seen.has(node)) return;
    seen.add(node);
    if (Array.isArray(node)) {
      let matched = false;
      for (const item of node) {
        if (item && typeof item === "object" && item.id && item.vendor) {
          index.set(String(item.id), {
            vendor: item.vendor,
            type: item.type ?? item.product_type ?? "",
            // For the modal, which renders a title and no id. Measured on the live page:
            // every product object carries `title`, and `productType` is the spelling the
            // Storefront payload uses where the card path sees `type`.
            title: item.title ?? "",
            // For the sold-out sizes, which have to come from Shopify — the app's payload
            // carries only the variants it means to sell.
            handle: item.handle ?? "",
            // For the modal's Added state: slots record a product id, the modal shows none.
            id: String(item.id),
            // For the modal's Added state: slots record a product id, the modal shows none.
            id: String(item.id),
          });
          matched = true;
        }
      }
      if (matched) return;
      for (const item of node.slice(0, 40)) walk(item, depth + 1);
      return;
    }
    for (const key of Object.keys(node)) walk(node[key], depth + 1);
  };
  walk(root, 0);
  return index;
}

/**
 * The same products keyed by rendered title, for the modal.
 *
 * The modal is the one surface with no product id anywhere on it — measured on the live
 * page, it renders a title, an inventory line, a price and a button, and nothing carries
 * the id the cards use. Its title is the exact `title` off the product object, so that is
 * the join. Lowercased and whitespace-collapsed because the two are rendered by different
 * code paths and only have to agree on the words.
 *
 * Entries without a title are skipped rather than keyed on "", which would otherwise let
 * one untitled product answer for every lookup that missed.
 */
export function titleIndex(index) {
  const byTitle = new Map();
  if (!index) return byTitle;
  for (const product of index.values()) {
    const key = normalizeTitle(product?.title);
    if (key) byTitle.set(key, product);
  }
  return byTitle;
}

/** Titles compared on their words alone. */
export function normalizeTitle(title) {
  if (typeof title !== "string") return "";
  return title.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Which slide the app is showing, read from its own markup.
 *
 * `gbbHidden` is the class it puts on every slide but the live one — measured on the live
 * page, that class carries `display: none`, so it is both what the app means and what the
 * shopper sees. Reading it rather than tracking a copy is what keeps the swipe, the arrows
 * and the thumbnails from drifting apart.
 *
 * Falls back to the first slide when nothing is marked, which is the state the app renders
 * for a single image.
 */
export function activeSlideIndex(slides) {
  if (!slides?.length) return 0;
  const found = [...slides].findIndex(
    (slide) => !slide?.classList?.contains("gbbHidden"),
  );
  return found < 0 ? 0 : found;
}

/**
 * Where a swipe lands: the current slide moved one step, clamped to the ends.
 *
 * Clamped rather than wrapping because the track under the photo is a PROGRESS bar — the
 * last segment lit has to mean "this is the last photo". Wrapping would light the first
 * segment again and read as a reset.
 *
 * A drag shorter than the threshold is not a swipe and returns the current slide, so a tap
 * that moves a few pixels does not change the photo.
 */
export function slideAfterDrag(current, dx, count, threshold = 40) {
  if (!Number.isFinite(current) || !Number.isFinite(count) || count < 1) return 0;
  const here = Math.min(Math.max(Math.trunc(current), 0), count - 1);
  if (!Number.isFinite(dx) || Math.abs(dx) < threshold) return here;
  // Dragging left (negative dx) moves forward, the way every photo carousel behaves.
  const next = dx < 0 ? here + 1 : here - 1;
  return Math.min(Math.max(next, 0), count - 1);
}

/**
 * The product ids the bundle box currently holds, read from the footer's filled slots.
 *
 * This has to be derived because nothing else records it. Measured on the live page: adding
 * a product changes NOTHING on its card — same classes, same attributes — and the app-global
 * JS's .tbInCart marks the SHOPIFY cart, which stays empty until the shopper hits Add To
 * Cart at the end. So a card whose product is already in the box looked exactly as
 * available as one that was not. The footer's `.gbbProductSlotContainer[data-product-id]`
 * is the one place the picked set is written down.
 */
export function productsInBox(root = document) {
  return new Set(
    [
      ...root.querySelectorAll(
        ".gbbBundleFooter .gbbProductSlotContainer[data-product-id]",
      ),
    ].map((slot) => slot.dataset.productId),
  );
}

/**
 * Toggles IN_BOX_CLASS on each card. Safe against the observer that calls it: the observer
 * watches childList only, so a class write cannot re-enter it, and toggle with an explicit
 * force is a no-op when the state already matches.
 */
function markProductsInBox(cards) {
  const held = productsInBox();
  for (const card of cards)
    card.classList.toggle(IN_BOX_CLASS, held.has(card.dataset.productId));
}

/**
 * The price a card shows, in whole cents, or null when it has none to read.
 * Parsed off the rendered text so it works whether or not the cents trim has run yet.
 */
export function cardPriceCents(text) {
  if (typeof text !== "string") return null;
  const m = text.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const value = Number(m[1]);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
}

/**
 * Puts the grid in price order, dearest first, by moving the nodes.
 *
 * The CSS `order` property was the obvious tool and does not work here: set to opposite
 * extremes with !important on two adjacent cards, they do not move, and every card reports
 * a computed order of 0 while carrying the inline value. Nothing in the app's stylesheets
 * sets `order`, and a bare probe div appended to the same grid honours it — so the property
 * is inert on these cards for a reason the CSSOM does not show. Moving nodes is the tool
 * that demonstrably works.
 *
 * A no-op when the order is already right, which is what keeps this off the observer's
 * back: reordering is a childList mutation, so an unconditional sort would re-trigger the
 * observer that called it and never settle.
 *
 * Sorted on a decorated copy rather than by comparator alone, so equal prices keep their
 * existing sequence — the app's own order still breaks ties. A card with no readable price
 * sorts last rather than first.
 */
/**
 * How long the grid must stop growing before the sort is allowed to run.
 *
 * Tapping a subcategory chip makes subcategory-filter.js page the app's loader until the
 * whole group has rendered — so cards keep arriving for seconds AFTER the tap, while the
 * shopper is already scrolling. Sorting on every arrival re-ordered the grid under their
 * thumb: measured on the live page, ONE card arriving moved 26 already-visible cards by up
 * to 386px, a full row. That is the jumping.
 *
 * 400ms is comfortably longer than the gap between pages and short enough that the order is
 * right before anyone reaches the end of the first screen.
 */
const SORT_SETTLE_MS = 400;
let lastCardCount = -1;
let lastCountChangeAt = 0;
let sortTimer = null;

/**
 * Whether the grid has stopped growing, and so whether sorting is safe.
 *
 * Measured in WALL TIME, not in passes, and that distinction is the whole point. The first
 * cut compared the count against the previous pass — but one 58-card page lands as several
 * mutation records, so the second record already saw an unchanged count and sorted 34ms
 * after the batch instead of 400ms after it. Comparing passes cannot tell "nothing has
 * arrived for a while" from "nothing arrived since the last microtask".
 */
export function gridSettled(count, previous, msSinceChange, settleMs = SORT_SETTLE_MS) {
  if (!Number.isFinite(count) || count !== previous) return false;
  return Number.isFinite(msSinceChange) && msSinceChange >= settleMs;
}

/**
 * Whether the shopper is still at the head of the grid, where reordering is free.
 *
 * `tolerance` covers the first row or so: a few hundred pixels in, the cards on screen are
 * still the ones a sort would put there anyway. Anything it cannot read counts as scrolled,
 * because leaving the order alone is the safe direction — a grid that never re-sorts merely
 * lists in the app's order, while one that re-sorts at the wrong moment moves what someone
 * is reading.
 */
export function atGridHead(scrollY, gridTop, tolerance = 200) {
  if (!Number.isFinite(scrollY) || !Number.isFinite(gridTop)) return false;
  return scrollY <= gridTop + tolerance;
}

function orderByPriceDescending(cards) {
  const container = cards[0]?.parentElement;
  if (!container) return;

  // Hold off while cards are still streaming in — see SORT_SETTLE_MS.
  const now = performance.now();
  if (cards.length !== lastCardCount) {
    lastCardCount = cards.length;
    lastCountChangeAt = now;
  }
  if (!gridSettled(cards.length, lastCardCount, now - lastCountChangeAt)) {
    clearTimeout(sortTimer);
    sortTimer = setTimeout(apply, SORT_SETTLE_MS);
    return;
  }

  /*
   * And never reorder under a thumb that has already moved past the top.
   *
   * The settle above collapses many jumps into one, but one is still a jump if it lands
   * while someone is reading. Anchoring the viewport was tried and measured, and it is the
   * wrong tool: holding the topmost visible card still across a full re-sort took a 7,071px
   * scroll on the live grid. That trades a shifted row for being teleported down the page.
   *
   * So the sort only runs while the shopper is still at the head of the grid, where a
   * reorder is what they came for and costs nothing. Once they are into the list the order
   * they are reading is left exactly as it is; later arrivals simply append. Tapping another
   * chip returns them to the top and the order is right again.
   */
  if (!atGridHead(window.scrollY, container.getBoundingClientRect().top + window.scrollY))
    return;

  const decorated = [...cards].map((card, i) => ({
    card,
    i,
    cents:
      cardPriceCents(card.querySelector(".gbbProductPrice")?.textContent ?? "") ??
      -1,
  }));
  decorated.sort((a, b) => b.cents - a.cents || a.i - b.i);

  if (decorated.every((d, i) => d.i === i)) return; // already in order

  const frag = document.createDocumentFragment();
  for (const d of decorated) frag.appendChild(d.card);
  container.appendChild(frag);
}

/** The grid's own heading, above the chips. */
export const GRID_HEADING_CLASS = "tbGridHeading";
export const GRID_HEADING_TEXT = "Choose Any Five Below";

/**
 * Puts the heading immediately above the chip bar, or above the grid when the filter is
 * not on the page.
 *
 * Anchored to one of those two rather than to the container, so it cannot outlive what it
 * labels: if the catalog stands down and takes the bar and the grid with it, there is no
 * anchor and no heading — a bare "Choose Any Five Below" over nothing would read as a bug.
 *
 * Re-seated rather than re-created when the app rebuilds around it, so the page never ends
 * up with two, and idempotent so the observer that calls this settles on the next pass.
 */
function ensureGridHeading() {
  const anchor =
    document.querySelector(".tbSubcatBar") ??
    document.querySelector(".gbbProductsContainer");
  if (!anchor?.parentElement) return;

  const existing = anchor.parentElement.querySelector(`.${GRID_HEADING_CLASS}`);
  if (existing) {
    if (existing.nextElementSibling !== anchor)
      anchor.parentElement.insertBefore(existing, anchor);
    return;
  }
  const heading = document.createElement("h2");
  heading.className = GRID_HEADING_CLASS;
  heading.textContent = GRID_HEADING_TEXT;
  anchor.parentElement.insertBefore(heading, anchor);
}

/** Chip labels rewritten in place; their data-tb-value — what the filter keys on — is untouched. */
function relabelChips() {
  for (const chip of document.querySelectorAll(".tbSubcatChip[data-tb-value]")) {
    const label = CHIP_LABELS[chip.dataset.tbValue];
    if (label && chip.textContent.trim() !== label) chip.textContent = label;
  }
}

/** Styled in tb-custom.css. */
const SLIDER_CLASS = "tbGallerySlider";
const SEGMENT_CLASS = "tbGallerySegment";
const WIRED_ATTR = "tbGalleryWired";
const MODAL_TITLE_ATTR = "tbModalSplit";

/**
 * The modal's title, split the same way the cards are.
 *
 * Joined by title rather than id because the modal renders no id (see titleIndex). A
 * product that cannot be found, or a title that does not have the shape splitTitle wants,
 * is left exactly as the app rendered it — an unsplit title reads fine, a wrong one does
 * not.
 */
function splitModalTitle(byTitle) {
  const el = document.querySelector(
    ".gbbProductVariantModalMainContainer .gbbVariantModalTitle",
  );
  // Re-checked against the rendered text, not just the flag: the app reuses this one
  // element for whichever product is opened next, so a flag alone would leave the second
  // product wearing the first one's name.
  if (!el || el.dataset[MODAL_TITLE_ATTR] === normalizeTitle(el.textContent))
    return;

  const product = byTitle.get(normalizeTitle(el.textContent));
  if (!product) return;
  const parts = splitTitle(el.textContent, product.vendor, product.type);
  if (!parts) return;

  const name = document.createElement("span");
  name.className = NAME_CLASS;
  name.textContent = parts.name;
  const meta = document.createElement("span");
  meta.className = META_CLASS;
  meta.textContent = parts.meta;
  // Flagged with what the SPLIT renders, so the mutation this causes matches the flag and
  // the observer settles instead of re-entering.
  el.dataset[MODAL_TITLE_ATTR] = normalizeTitle(`${parts.name} ${parts.meta}`);
  // Stashed because this split DESTROYS the raw title, which was the only thing on the
  // modal identifying the product. Anything downstream that needs to know which product is
  // open — the sold-out sizes do — reads it from here rather than re-parsing the caption.
  if (product.handle) el.dataset.tbHandle = product.handle;
  if (product.type) el.dataset.tbType = product.type;
  if (product.id) el.dataset.tbProductId = product.id;
  if (product.id) el.dataset.tbProductId = product.id;
  el.replaceChildren(name, meta);
}


/**
 * The alpha size scale, in the order a shopper expects to read it.
 *
 * Combination sizes sit where they belong rather than alphabetically — S/M between S and M,
 * L/XL between L and XL — because that is where a shopper looks for them.
 */
const ALPHA_SIZES = [
  "xs",
  "s",
  "s/m",
  "m",
  "l",
  "l/xl",
  "xl",
  "2xl",
  "3xl",
  "4xl",
  "5xl",
];

/**
 * A sortable rank for one size label: [group, value, key].
 *
 * Three groups because this catalog runs three vocabularies — measured across all 108
 * products: alpha (XS..3XL, plus S/M and L/XL), numeric (shoe sizes 7..14 including 9.5,
 * and waist sizes 30..36), and a handful that are neither ("O/S", "Default Title"). A
 * single numeric scale would sort "10" before "9", and a single alphabetic sort would put
 * "2XL" before "S".
 *
 * Grouping keeps the comparison total even for a product that somehow mixes them, and the
 * key breaks ties so the order is stable rather than dependent on input sequence.
 */
export function sizeRank(label) {
  if (typeof label !== "string") return [3, 0, ""];
  const key = label.trim().toLowerCase();
  if (!key) return [3, 0, ""];
  const alpha = ALPHA_SIZES.indexOf(key);
  if (alpha >= 0) return [0, alpha, key];
  // Leading digit only, so "11 (2)" ranks with 11 and "O/S" does not rank as 0.
  if (/^\d/.test(key)) {
    const num = Number.parseFloat(key);
    if (Number.isFinite(num)) return [1, num, key];
  }
  return [2, 0, key];
}

/** Comparator over size labels, for Array#sort. */
export function compareSizes(a, b) {
  const ra = sizeRank(a);
  const rb = sizeRank(b);
  if (ra[0] !== rb[0]) return ra[0] - rb[0];
  if (ra[1] !== rb[1]) return ra[1] - rb[1];
  return ra[2] < rb[2] ? -1 : ra[2] > rb[2] ? 1 : 0;
}

/**
 * The sizes Shopify says are sold out and the modal is not already showing.
 *
 * Only variants EXPLICITLY unavailable are returned. A variant Shopify calls available but
 * the app chose not to render is left alone — the app owns what is sellable here (bundle
 * rules, inventory policy), and inventing a selectable chip for it would offer a size the
 * box cannot actually take.
 */
export function soldOutSizes(renderedLabels, variants) {
  const shown = new Set(
    (renderedLabels ?? []).map((l) => String(l ?? "").trim().toLowerCase()),
  );
  const out = [];
  for (const variant of variants ?? []) {
    if (!variant || variant.available !== false) continue;
    const title = String(variant.title ?? "").trim();
    if (!title || shown.has(title.toLowerCase())) continue;
    if (!out.includes(title)) out.push(title);
  }
  return out;
}

/**
 * The size run each product type is sold in, so every product of a type shows the same row.
 *
 * WHY THIS IS NEEDED. The modal showed exactly the variants Shopify holds, and those vary
 * per product — measured across the catalog, SS tees alone carry SEVEN different curves
 * across 49 products (M-3XL on 16, XS,M-3XL on 10, M-2XL on 8, and so on). A shopper reading
 * two tees saw two different rows and no way to tell a size that sold out from one that was
 * never made.
 *
 * Derived from the catalog on 2026-08-18 as the union of ALPHA sizes carried per type; to
 * regenerate, take the distinct variant titles per product_type.
 *
 * ONLY THE ALPHA SCALE IS NORMALISED, and two measured cases say why:
 *   - Swim Shorts runs TWO sizing systems — S,M,L on some, waist 30-36 on others. A blanket
 *     curve would strike out S,M,L on a waist-sized short, which is not a missing size but a
 *     different system.
 *   - Slides are numeric with 12 distinct values across 8 products (including a "11 (2)"
 *     data artifact), so a full curve would strike out roughly five sizes on every pair.
 * Types absent here — the O/S hats, sunglasses — normalise to nothing, which is correct:
 * there is no run to complete.
 */
export const HOUSE_SIZE_CURVES = {
  "ss tees": ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  shorts: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
  "swim shorts": ["S", "M", "L"],
  "bucket hats": ["S/M", "L/XL"],
};

/**
 * The house run for a product, or [] when it has none to complete.
 *
 * Returns [] unless the product actually uses the run — that is what keeps a waist-sized
 * swim short (30-36) from being handed the S,M,L curve its type also contains.
 */
export function houseCurve(type, carried) {
  const curve = HOUSE_SIZE_CURVES[String(type ?? "").trim().toLowerCase()];
  if (!curve) return [];
  const uses = (carried ?? []).some((size) =>
    curve.some((c) => c.toLowerCase() === String(size ?? "").trim().toLowerCase()),
  );
  return uses ? curve : [];
}

/**
 * Sizes in the house run that this product does not carry at all.
 *
 * Distinct from soldOutSizes: those exist as variants and have run out, these were never
 * made. Both render struck through — to a shopper the outcome is the same — but they are
 * found differently, because a size that is not a variant has nothing to read availability
 * from.
 */
export function uncarriedSizes(renderedLabels, variants, curve) {
  const shown = new Set(
    (renderedLabels ?? []).map((l) => String(l ?? "").trim().toLowerCase()),
  );
  const known = new Set(
    (variants ?? [])
      .filter(Boolean)
      .map((v) => String(v.title ?? "").trim().toLowerCase()),
  );
  const out = [];
  for (const size of curve ?? []) {
    const key = String(size ?? "").trim().toLowerCase();
    if (!key || shown.has(key) || known.has(key)) continue;
    if (!out.includes(size)) out.push(size);
  }
  return out;
}

const SOLD_OUT_CLASS = "tbSizeSoldOut";
const WAIST_NOTE_CLASS = "tbSizeWaistNote";

/**
 * The chosen size's waist measurement, on its own line under the size row and empty for a size
 * the warehouse has no mapping for, so the buttons themselves stay a uniform row of letters.
 */
function paintWaistNote(row, variants, label) {
  const map = globalThis.__TB_SIZE_MAP__;
  if (!map) return;
  let note = row.parentElement?.querySelector(`.${WAIST_NOTE_CLASS}`);
  if (!note) {
    note = document.createElement("div");
    note.className = WAIST_NOTE_CLASS;
    // Polite rather than assertive: the measurement follows a size the shopper just chose.
    note.setAttribute("aria-live", "polite");
    row.insertAdjacentElement("afterend", note);
  }
  const chosen = [...row.querySelectorAll(".gbbProductVariantOptionValue")].find(
    (chip) => chip.getAttribute("aria-checked") === "true",
  );
  const size = chosen ? label(chosen) : "";
  const variant = size
    ? variants.find((v) => String(v?.title ?? "").trim() === size)
    : null;
  const waist = variant ? map[String(variant.sku ?? "").trim()] : null;
  const text = waist ? `${size} · ${waist}` : "";
  if (note.textContent !== text) note.textContent = text;
}
/** handle -> promise of Shopify's variant list, so a modal reopened costs no second fetch. */
const variantCache = new Map();

function shopifyVariants(handle) {
  if (!variantCache.has(handle)) {
    variantCache.set(
      handle,
      fetch(`/products/${encodeURIComponent(handle)}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => data?.variants ?? null)
        // A failed lookup resolves to null rather than rejecting: the row keeps showing the
        // in-stock sizes it already had, which is exactly today's behaviour.
        .catch(() => null),
    );
  }
  return variantCache.get(handle);
}

/**
 * Adds the sold-out sizes to the modal's size row, struck through and inert, and puts the
 * whole row in size order.
 *
 * The app filters these out server-side — measured, its payload for a product with four
 * sold-out sizes carries two variants and an `options.values` of exactly those two — so the
 * real list has to come from Shopify's own product endpoint.
 *
 * The injected chips deliberately do NOT carry `data-option-value-id`, which is what the
 * app's delegated handler keys on. That, `aria-disabled`, `tabindex="-1"` and
 * pointer-events:none in the stylesheet are four independent reasons a shopper cannot pick
 * one — this must never put an unbuyable size into a box.
 */
function paintSoldOutSizes(byTitle) {
  const row = document.querySelector(
    ".gbbProductVariantModalMainContainer .gbbProductVariantOptionValuesContainer",
  );
  if (!row) return;
  const real = [...row.querySelectorAll(".gbbProductVariantOptionValue")].filter(
    (chip) => !chip.classList.contains(SOLD_OUT_CLASS),
  );
  // Nothing rendered yet: the app has not built the row, so there is nothing to complete.
  if (!real.length) return;

  const titleEl = document.querySelector(
    ".gbbProductVariantModalMainContainer .gbbVariantModalTitle",
  );
  // The stash first: by now splitModalTitle has usually replaced the raw title with the
  // name/caption pair, so looking the product up by rendered text would miss. The title
  // lookup stays as the fallback for a product whose title did not split.
  const stashed = byTitle.get(normalizeTitle(titleEl?.textContent));
  const handle = titleEl?.dataset.tbHandle || stashed?.handle;
  const type = titleEl?.dataset.tbType || stashed?.type;
  if (!handle) return;

  const variants = variantCache.get(handle);
  if (!variants) {
    // First sight of this product: warm the cache, then repaint when it lands.
    shopifyVariants(handle).then((list) => {
      if (list) variantCache.set(handle, list);
      apply();
    });
    return;
  }
  if (typeof variants.then === "function") return; // still in flight

  const label = (chip) =>
    chip.dataset.optionValueId ?? chip.textContent.trim();
  const rendered = real.map(label);
  const curve = houseCurve(
    type,
    variants.map((v) => v?.title),
  );
  // Sold out, then never carried — both struck through, found two different ways.
  const missing = [
    ...soldOutSizes(rendered, variants),
    ...uncarriedSizes(rendered, variants, curve),
  ];

  for (const size of missing) {
    if (
      [...row.querySelectorAll(`.${SOLD_OUT_CLASS}`)].some(
        (c) => c.dataset.tbSize === size,
      )
    )
      continue;
    const chip = document.createElement("div");
    chip.className = `gbbProductVariantOptionValue ${SOLD_OUT_CLASS}`;
    chip.dataset.tbSize = size;
    chip.setAttribute("role", "radio");
    chip.setAttribute("aria-checked", "false");
    chip.setAttribute("aria-disabled", "true");
    chip.setAttribute("tabindex", "-1");
    chip.textContent = size;
    row.appendChild(chip);
  }

  paintWaistNote(row, variants, label);

  // Size order across the whole row, injected and real alike. A no-op when it already
  // matches, which is what keeps this off the observer's back.
  const chips = [...row.querySelectorAll(".gbbProductVariantOptionValue")];
  const sorted = [...chips].sort((a, b) => compareSizes(label(a), label(b)));
  if (sorted.every((chip, i) => chip === chips[i])) return;
  const frag = document.createDocumentFragment();
  for (const chip of sorted) frag.appendChild(chip);
  row.appendChild(frag);
}

/**
 * Whether Add To Box may proceed: "open" or "blocked".
 *
 * Blocked only in one precise state — two or more sizes a shopper could pick, and none of
 * them picked. Everything else is open, and that asymmetry is deliberate: blocking wrongly
 * costs the order, allowing wrongly costs nothing that was not already true yesterday.
 *
 * A product with a single sellable size stays open and keeps the app's pre-selection. There
 * is nothing to choose, so making someone tap the only option is friction with no payoff.
 */
export function sizeGate(chips) {
  if (!Array.isArray(chips)) return "open";
  const selectable = chips.filter((chip) => chip && !chip.soldOut);
  if (selectable.length < 2) return "open";
  return selectable.some((chip) => chip.active) ? "open" : "blocked";
}

const GATE_ATTR = "tbSizeUnchosen";
const CLEARED_ATTR = "tbSelectionCleared";
const PROMPT_CLASS = "tbSizePrompt";
const PROMPT_TEXT = "Select a size";
const FLASH_ATTR = "tbSizeFlash";

/** The chip shapes sizeGate reads, off the live row. */
function chipStates(row) {
  return [...row.querySelectorAll(".gbbProductVariantOptionValue")].map((chip) => ({
    soldOut: chip.classList.contains(SOLD_OUT_CLASS),
    active: chip.dataset.active === "true",
  }));
}

/**
 * Drops the app's opening pre-selection when there is a real choice to make, and holds Add
 * To Box until the shopper makes it.
 *
 * The app opens every modal with a size already selected — the first sellable one. On a
 * product with six sizes that is a wrong size one thumb-slip away from the box.
 *
 * DESELECTING ALONE WOULD BE WORSE, and this was measured rather than assumed: with every
 * chip's data-active cleared, Add To Box still added the item, because the app keeps its own
 * variant state and does not read the DOM. So an un-gated deselect would hide the
 * pre-selection instead of removing it. The gate is the feature; the deselect is half of it.
 *
 * The gate is JS, not CSS. Both halves live in this function, so if it never runs the app
 * keeps its pre-selection and the page behaves exactly as it did before — there is no state
 * where the selection is cleared but the button is not held. The stylesheet only makes the
 * held button LOOK held; it deliberately does not use pointer-events, so the click still
 * reaches the blocker below and the shopper gets told why nothing happened.
 */
function paintSizeChoice() {
  const modal = document.querySelector(".gbbProductVariantModalMainContainer");
  const row = modal?.querySelector(".gbbProductVariantOptionValuesContainer");
  const action = modal?.querySelector(".gbbVariantModalActionContainer");
  if (!modal || !row || !action) return;

  const chips = [...row.querySelectorAll(".gbbProductVariantOptionValue")];
  const selectable = chips.filter((chip) => !chip.classList.contains(SOLD_OUT_CLASS));

  // Once per product. The app builds a fresh row element for each modal it opens — verified
  // by node identity across two products — so this flag resets on its own and can never
  // clear a selection the shopper just made.
  if (selectable.length > 1 && !row.dataset[CLEARED_ATTR]) {
    row.dataset[CLEARED_ATTR] = "1";
    for (const chip of chips) {
      delete chip.dataset.active;
      chip.setAttribute("aria-checked", "false");
    }
  }

  const blocked = sizeGate(chipStates(row)) === "blocked";
  const button = action.querySelector(".gbbProductVariantAddButton");

  if (blocked) action.dataset[GATE_ATTR] = "1";
  else delete action.dataset[GATE_ATTR];
  if (button) {
    if (blocked) button.setAttribute("aria-disabled", "true");
    else button.removeAttribute("aria-disabled");
  }

  const existing = row.parentElement?.querySelector(`.${PROMPT_CLASS}`);
  if (blocked && !existing) {
    const prompt = document.createElement("p");
    prompt.className = PROMPT_CLASS;
    prompt.textContent = PROMPT_TEXT;
    row.after(prompt);
  } else if (!blocked && existing) {
    existing.remove();
  }
}

/**
 * Holds the click while no size is chosen.
 *
 * Capture phase on the document, so it runs before whatever the app has bound. It refuses
 * to act on anything it cannot read — no row, no chips, an unrecognised shape — because a
 * false block here is a dead Add To Box, which is the one outcome worse than the problem
 * being solved.
 */
function installSizeGuard(doc = document) {
  doc.addEventListener(
    "click",
    (event) => {
      const button = event.target?.closest?.(".gbbProductVariantAddButton");
      if (!button) return;
      const modal = button.closest(".gbbProductVariantModalMainContainer");
      // Already in the box: the cap enforced, not merely labelled.
      if (button.dataset[ADDED_ATTR]) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const row = modal?.querySelector(".gbbProductVariantOptionValuesContainer");
      if (!row) return; // no size to choose — a one-variant product — so never held
      if (sizeGate(chipStates(row)) !== "blocked") return;

      event.preventDefault();
      event.stopPropagation();
      // Say why nothing happened, rather than leaving a dead button.
      row.dataset[FLASH_ATTR] = "1";
      setTimeout(() => delete row.dataset[FLASH_ATTR], 1400);
    },
    true,
  );
}


/**
 * How the modal photo should fill its frame: "cover" to crop, "contain" to letterbox.
 *
 * Cover ONLY when the source is at least as tall, proportionally, as the frame — then the
 * overflow is vertical, so the crop takes an equal band off the top and bottom and the
 * product stays centred. A source WIDER than the frame would have to lose its left and
 * right edges instead, which on a centred product shot is the difference between a tighter
 * crop and a clipped garment, so those letterbox rather than crop.
 *
 * This is a runtime decision because CSS cannot select on a source's intrinsic ratio, and
 * the catalog is mid-migration: Shopify holds 4:5 for everything, while the bundle app is
 * still serving its cached square originals for most products. Ratio-aware means the ones
 * already converted crop correctly today, the rest stay uncropped, and nothing needs
 * changing again when the app's cache catches up.
 */
export function fitForRatio(width, height, frameRatio, tolerance = 0.02) {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return "contain";
  if (!Number.isFinite(frameRatio) || width <= 0 || height <= 0) return "contain";
  return width / height <= frameRatio + tolerance ? "cover" : "contain";
}

const FIT_WATCHED_ATTR = "tbFitWatched";
/** The card frame is exactly 4:5, so a 4:5 photo fills it with nothing cropped at all. */
const CARD_FRAME_RATIO = 4 / 5;

/**
 * Applies fitForRatio to every slide in the modal.
 *
 * Inline, because the answer differs per image and CSS has no selector for it. The
 * stylesheet still declares `contain`, so a browser that never runs this letterboxes rather
 * than cropping — the safe direction.
 *
 * A load listener per image because an image that has not decoded yet reports a natural
 * size of 0, and decoding is not a DOM mutation, so the observer driving apply() would
 * never revisit it.
 */
function paintPhotoFit() {
  const frame = document.querySelector(
    ".gbbProductVariantModalMainContainer .gbbProductVariantModalImageTitleContainer",
  );
  if (!frame) return;
  const box = frame.getBoundingClientRect();
  if (!box.height) return;
  const frameRatio = box.width / box.height;

  for (const slide of frame.querySelectorAll(".gbbCarouselSlide")) {
    if (!slide.dataset[FIT_WATCHED_ATTR]) {
      slide.dataset[FIT_WATCHED_ATTR] = "1";
      slide.addEventListener("load", () => paintPhotoFit(), { once: true });
    }
    if (!slide.naturalWidth) continue;
    const fit = fitForRatio(slide.naturalWidth, slide.naturalHeight, frameRatio);
    if (slide.style.objectFit !== fit) slide.style.objectFit = fit;
    /*
     * The blend goes with the fit. multiply exists to sink a cutout's white background into
     * the grey frame; on a photo that fills the frame there is no background left to sink,
     * and multiplying by #efefef just darkens the whole picture about 6% — visible on the
     * lifestyle shots in the new photography.
     */
    const blend = fit === "cover" ? "normal" : "multiply";
    if (slide.style.mixBlendMode !== blend) slide.style.mixBlendMode = blend;
  }
}

/**
 * The same rule for the grid cards, published as an attribute for the stylesheet.
 *
 * An attribute rather than inline styles because the card treatment is several declarations
 * per state, not one — see the two [data-tb-fit] blocks in tb-custom.css. The card frame is
 * exactly 4:5, so that is the ratio compared against.
 */
function paintCardFit() {
  for (const img of document.querySelectorAll(
    ".gbbProductItem .gbbProductImage",
  )) {
    if (!img.dataset[FIT_WATCHED_ATTR]) {
      img.dataset[FIT_WATCHED_ATTR] = "1";
      img.addEventListener("load", () => paintCardFit(), { once: true });
    }
    if (!img.naturalWidth) continue;
    const fit = fitForRatio(img.naturalWidth, img.naturalHeight, CARD_FRAME_RATIO);
    if (img.dataset.tbFit !== fit) img.dataset.tbFit = fit;
  }
}

/* ------------------------------------------------------------------------------
 * Bundle bar, button counter, retail line, fine print.
 *
 * These replace four pieces of copy that each explained the offer in their own words — a
 * struck price, a "Buy Three Get Two Free" tag, a "Limit 1 per order." line, and a sentence
 * of final-sale prose. The offer is now explained in exactly ONE place, the bar, so
 * "buy 3 get 2" can never be read as an instruction about the item on screen.
 * ---------------------------------------------------------------------------- */

export const BAR_HEADLINE = "Pick any 5 items";
export const BAR_SUBLINE = "Buy three, get two free";
export const KEEP_SHOPPING_TEXT = "Keep shopping";

/** What the box holds: { filled, required }, or null when the footer cannot be read. */
export function bundleState(doc = document) {
  const slots = doc.querySelectorAll(".gbbBundleFooter .gbbProductSlotContainer");
  if (!slots.length) return null;
  let filled = 0;
  for (const slot of slots) if (slot.dataset.productId) filled++;
  return { filled, required: slots.length };
}

/**
 * The Add button's label and whether it is held.
 *
 * Already in the box -> disabled, and the one-per-bundle cap explains itself at the moment
 * it binds rather than as a rule to read beforehand. A readable box -> the slot this tap
 * fills. Anything else -> a plain enabled "Add to bundle".
 *
 * That last case is the rule from tb-cart-button.js: a label that cannot be computed must
 * never disable the control. A wrong label is cosmetic; a disabled Add To Box is a lost
 * order.
 */
export function bundleButtonLabel(state, alreadyIn) {
  if (alreadyIn === true) return { text: "Added — max 1 per bundle", disabled: true };
  if (
    !state ||
    !Number.isFinite(state.filled) ||
    !Number.isFinite(state.required) ||
    state.required < 1 ||
    state.filled >= state.required
  )
    return { text: "Add to bundle", disabled: false };
  return {
    text: `Add to bundle — fills slot ${state.filled + 1} of ${state.required}`,
    disabled: false,
  };
}

/**
 * "$40 · takes 1 of your 5 slots".
 *
 * dropWholeCents is applied HERE as well as on the app's own price element, because the app
 * rewrites that element with cents on every size tap — this line is built from its text, so
 * without the trim a chosen size turned "$40" into "$40.00 · takes 1 of your 5 slots".
 */
export function retailLine(priceText, required) {
  const price = dropWholeCents(String(priceText ?? "").trim());
  if (!price) return "";
  const slots = Number.isFinite(required) && required > 0 ? required : null;
  return slots ? `${price} · takes 1 of your ${slots} slots` : price;
}

export const DELIVERY_LABEL = "Expected delivery";
export const FINAL_SALE_TEXT =
  "This item is FINAL SALE and is not eligible for additional discounts, exchanges, or refunds";
export const FINAL_SALE_EMPHASIS = "FINAL SALE";

/**
 * A sentence split into [before, phrase, after] around the phrase to emphasise. A phrase
 * that is absent returns the whole sentence as `before`, so an edit to the copy still
 * renders the full line rather than a blank or a truncated one.
 */
export function emphasisSplit(text, phrase) {
  if (typeof text !== "string") return ["", "", ""];
  if (typeof phrase !== "string" || !phrase) return [text, "", ""];
  const at = text.indexOf(phrase);
  if (at < 0) return [text, "", ""];
  return [text.slice(0, at), phrase, text.slice(at + phrase.length)];
}

/**
 * The delivery window as "MM/DD – MM/DD", 4 to 7 local calendar days out.
 *
 * Built from calendar parts: `new Date(y, m, d + n)` normalises the overflow itself, so month
 * ends, year ends and leap days come out right and no daylight-saving hour can push a date
 * across midnight. `now` is a parameter so the window is testable.
 */
export function deliveryRange(now, fromDays = 4, toDays = 7) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;
  if (!Number.isFinite(fromDays) || !Number.isFinite(toDays)) return null;
  const at = (days) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  };
  return `${at(fromDays)} – ${at(toDays)}`;
}

const TOPBAR_CLASS = "tbModalTopBar";
const BAR_CLASS = "tbBundleBar";
const DOTS_CLASS = "tbBundleDots";
const RETAIL_CLASS = "tbRetailLine";
const FINE_PRINT_CLASS = "tbFinePrint";
const KEEP_SHOPPING_CLASS = "tbKeepShopping";
const ADDED_ATTR = "tbAdded";
const CHECK_SVG = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5 6.5 11.5 12.5 5"/></svg>';
const TRUCK_SVG = '<svg viewBox="0 0 32 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 17h9V5H9"/><path d="M18 9h4.5l3.5 3.5V17h-2.5"/><circle cx="11.5" cy="17.5" r="2"/><circle cx="22" cy="17.5" r="2"/><path d="M2 8h5M1 12h4M3 16h3"/></svg>';
const TAG_SVG = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 9.2V3h6.2l8.3 8.3a1.5 1.5 0 0 1 0 2.1l-4.1 4.1a1.5 1.5 0 0 1-2.1 0z"/><circle cx="6.2" cy="6.2" r="1.1"/></svg>';

/**
 * The bundle bar: the one place the offer is explained.
 *
 * Persistent and above the product, because the offer is a fact about the BOX, not about the
 * item being looked at. Stated beside a product it reads as an instruction about that
 * product — "buy three of these".
 *
 * The dots render only when the box can actually be read. Five empty dots drawn from a guess
 * would understate a box that already holds picks, and the copy explains the offer perfectly
 * well without them.
 */
function paintBundleBar(doc = document) {
  const sheet = doc.querySelector(
    ".gbbProductVariantModalMainContainer .gbbProductVariantModalContainer",
  );
  if (!sheet) return;

  /*
   * A row of its own for the close control, above the bundle bar. It used to sit absolutely
   * over whatever was beneath it — first the photo, then the pick dots — so it gets a row
   * instead, and the app's OWN button is moved into it rather than replaced, which leaves
   * the app's close handler intact.
   */
  let top = sheet.querySelector(`:scope > .${TOPBAR_CLASS}`);
  if (!top) {
    top = doc.createElement("div");
    top.className = TOPBAR_CLASS;
    sheet.prepend(top);
  } else if (top !== sheet.firstElementChild) {
    sheet.prepend(top);
  }
  const close = sheet.querySelector(".gbbProductVariantModalClose");
  if (close && close.parentElement !== top) top.appendChild(close);

  let bar = sheet.querySelector(`:scope > .${BAR_CLASS}`);
  if (!bar) {
    bar = doc.createElement("div");
    bar.className = BAR_CLASS;
    const copy = doc.createElement("div");
    const headline = doc.createElement("p");
    headline.className = "tbBundleBarHeadline";
    headline.textContent = BAR_HEADLINE;
    const sub = doc.createElement("p");
    sub.className = "tbBundleBarSub";
    sub.textContent = BAR_SUBLINE;
    copy.append(headline, sub);
    const dots = doc.createElement("div");
    dots.className = DOTS_CLASS;
    bar.append(copy, dots);
    top.after(bar);
  } else if (bar.previousElementSibling !== top) {
    top.after(bar); // the app rebuilt around it
  }

  const dots = bar.querySelector(`.${DOTS_CLASS}`);
  const state = bundleState(doc);
  if (!state) {
    if (dots.childElementCount) dots.replaceChildren();
    return;
  }
  if (dots.childElementCount !== state.required) {
    const next = [];
    for (let i = 0; i < state.required; i++) {
      const dot = doc.createElement("span");
      dot.className = "tbBundleDot";
      dot.innerHTML = CHECK_SVG;
      next.push(dot);
    }
    dots.replaceChildren(...next);
  }
  [...dots.children].forEach((dot, i) => {
    if (i < state.filled) dot.dataset.tbOn = "1";
    else delete dot.dataset.tbOn;
  });
}

/** Whether the product this modal shows is already in the box. Null when unreadable. */
function alreadyInBox(doc = document) {
  const id = doc.querySelector(
    ".gbbProductVariantModalMainContainer .gbbVariantModalTitle",
  )?.dataset.tbProductId;
  if (!id) return null;
  return productsInBox(doc).has(id);
}

/**
 * The button's counter and Added state, the retail line, and the fine-print row.
 *
 * The size gate is respected rather than fought — it owns its own held state and this adds
 * to it, so a button can be held for a missing size AND carry the slot counter.
 */
function paintOfferAndFinePrint(now = new Date(), doc = document) {
  const modal = doc.querySelector(".gbbProductVariantModalMainContainer");
  const action = modal?.querySelector(".gbbVariantModalActionContainer");
  const button = action?.querySelector(".gbbProductVariantAddButton");
  if (!modal || !action || !button) return;

  const state = bundleState(doc);
  const { text, disabled } = bundleButtonLabel(state, alreadyInBox(doc));

  if (button.textContent.trim() !== text) button.textContent = text;
  if (disabled) button.dataset[ADDED_ATTR] = "1";
  else delete button.dataset[ADDED_ATTR];
  const held = disabled || action.dataset.tbSizeUnchosen === "1";
  if (held) button.setAttribute("aria-disabled", "true");
  else button.removeAttribute("aria-disabled");

  let keep = action.querySelector(`.${KEEP_SHOPPING_CLASS}`);
  if (disabled && !keep) {
    keep = doc.createElement("button");
    keep.type = "button";
    keep.className = KEEP_SHOPPING_CLASS;
    keep.textContent = KEEP_SHOPPING_TEXT;
    keep.addEventListener("click", () => {
      modal.querySelector(".gbbProductVariantModalClose")?.click();
    });
    button.after(keep);
  } else if (!disabled && keep) {
    keep.remove();
  }

  const row = modal.querySelector(".gbbProductPricesContainer");
  if (row) {
    const line = retailLine(
      row.querySelector(".gbbProductPrice")?.textContent?.trim(),
      state?.required,
    );
    let el = row.querySelector(`.${RETAIL_CLASS}`);
    if (line && !el) {
      el = doc.createElement("p");
      el.className = RETAIL_CLASS;
      row.appendChild(el);
    }
    if (el && line && el.textContent !== line) el.textContent = line;
    if (el && !line) el.remove();
  }

  let fine = action.querySelector(`.${FINE_PRINT_CLASS}`);
  if (!fine) {
    fine = doc.createElement("div");
    fine.className = FINE_PRINT_CLASS;
    fine.innerHTML =
      `<p class="tbFinePrintRow"><span class="tbFinePrintIcon">${TRUCK_SVG}</span>` +
      `<span><span data-tb-label></span> <span class="tbDeliveryDates" data-tb-dates></span></span></p>` +
      `<p class="tbFinePrintRow"><span class="tbFinePrintIcon">${TAG_SVG}</span><span data-tb-terms></span></p>`;
    action.appendChild(fine);
  }
  const range = deliveryRange(now);
  const labelEl = fine.querySelector("[data-tb-label]");
  const datesEl = fine.querySelector("[data-tb-dates]");
  // No readable clock means no delivery promise, rather than a wrong one.
  if (labelEl.textContent !== DELIVERY_LABEL) labelEl.textContent = DELIVERY_LABEL;
  if (datesEl.textContent !== (range ?? "")) datesEl.textContent = range ?? "";
  labelEl.closest(".tbFinePrintRow").hidden = !range;

  const termsEl = fine.querySelector("[data-tb-terms]");
  if (!termsEl.childElementCount) {
    const [head, phrase, tail] = emphasisSplit(FINAL_SALE_TEXT, FINAL_SALE_EMPHASIS);
    if (phrase) {
      const strong = doc.createElement("strong");
      strong.className = "tbFinalSaleEmphasis";
      strong.textContent = phrase;
      termsEl.append(head, strong, tail);
    } else {
      termsEl.textContent = head;
    }
  }
}

/** Lights the segment for the slide on show. */
function paintSegments(slider, index) {
  [...slider.children].forEach((segment, i) => {
    if (i === index) segment.dataset.tbOn = "1";
    else delete segment.dataset.tbOn;
  });
}

/**
 * The swipe hint under the photo, and the swipe itself.
 *
 * Gated on there being something to swipe TO. With one image this tears the whole thing
 * back down — a progress bar over a single photo is a promise the page cannot keep, and as
 * of 2026-08-16 that is 104 of the 106 products. It arrives on its own when the 4:5
 * photography ships and the app renders a second slide.
 *
 * THE APP OWNS WHICH SLIDE SHOWS. This follows it; it does not compete with it. The first
 * cut of this drove visibility itself with inline opacity, on the assumption that the fade
 * classes were opacity — they are not: the app hides a slide with `gbbHidden`, which sets
 * `display: none`, and no opacity can outrank that. Swiping moved the lit segment onto a
 * blank frame, and the app's own arrows did nothing because this kept overwriting them.
 *
 * So a swipe clicks the app's own thumbnail for the target slide — which works even though
 * the strip is `display: none` on mobile, because the handler does not care whether it is
 * painted — and the segments are read back off whichever slide is not `gbbHidden`. That
 * leaves one source of truth, so the arrows, the thumbnails and the swipe cannot disagree.
 */
function paintGallery() {
  const frame = document.querySelector(
    ".gbbProductVariantModalMainContainer .gbbProductVariantModalImageTitleContainer",
  );
  const sheet = frame?.closest(".gbbProductVariantModalContainer");
  if (!frame || !sheet) return;

  const slides = [...frame.querySelectorAll(".gbbCarouselSlide")];
  const existing = sheet.querySelector(`.${SLIDER_CLASS}`);

  if (slides.length < 2) {
    existing?.remove();
    return;
  }

  let slider = existing;
  // Rebuilt when the count changes, which is how one product's gallery becomes the next
  // one's — the app reuses this modal rather than building a new one per product.
  if (!slider || slider.children.length !== slides.length) {
    slider?.remove();
    slider = document.createElement("div");
    slider.className = SLIDER_CLASS;
    for (let i = 0; i < slides.length; i++) {
      const segment = document.createElement("span");
      segment.className = SEGMENT_CLASS;
      slider.appendChild(segment);
    }
    frame.after(slider);
  }

  paintSegments(slider, activeSlideIndex(slides));

  if (frame.dataset[WIRED_ATTR]) return;
  frame.dataset[WIRED_ATTR] = "1";

  // Repaint after anything the app handles itself — an arrow, a thumbnail, or the swipe
  // below, which lands here too because the click it dispatches bubbles. Capture phase and
  // two delays so the read happens after the fade has settled, not during it.
  frame.addEventListener(
    "click",
    () => {
      for (const delay of [0, 260]) setTimeout(paintGallery, delay);
    },
    true,
  );

  const wrapper = frame.querySelector(".gbbCarouselMain");
  if (!wrapper) return;
  let startX = null;
  wrapper.addEventListener("pointerdown", (e) => {
    startX = e.clientX;
  });
  wrapper.addEventListener("pointerup", (e) => {
    if (startX === null) return;
    const dx = e.clientX - startX;
    startX = null;
    const live = [...frame.querySelectorAll(".gbbCarouselSlide")];
    const here = activeSlideIndex(live);
    const next = slideAfterDrag(here, dx, live.length);
    if (next === here) return;
    frame
      .querySelector(`.gbbCarouselThumb[data-slide-index="${next}"]`)
      ?.click();
  });
  wrapper.addEventListener("pointercancel", () => {
    startX = null;
  });
}

function apply() {
  // Ahead of the card work: the chip bar exists on its own schedule, and a run that finds
  // no cards yet must still have relabelled it and seated the heading.
  ensureGridHeading();
  relabelChips();

  // Built at most once per pass and only if something actually needs it, so a pass with
  // nothing left to do costs a couple of querySelectorAlls.
  let index;
  const products = () =>
    (index ??= productIndex(window.easybundle_active_bundle_data));

  // Before the cards guard: the modal outlives a rebuild that momentarily empties the
  // grid, and its gallery needs repainting on every pass either way.
  paintGallery();
  paintPhotoFit();
  paintCardFit();
  paintBundleBar();
  paintOfferAndFinePrint();
  if (
    document.querySelector(
      ".gbbProductVariantModalMainContainer .gbbVariantModalTitle",
    )
  ) {
    const byTitle = titleIndex(products());
    splitModalTitle(byTitle);
    paintSoldOutSizes(byTitle);
    // After the sold-out chips exist, so they are excluded from what counts as a choice.
    paintSizeChoice();
  }

  const cards = document.querySelectorAll(".gbbProductItem[data-product-id]");
  if (!cards.length) return;

  // Prices carry their own flag: the app re-renders a card's price independently of its
  // title, so sharing the title's flag would leave a repainted price at "$25.00".
  //
  // Not scoped to .gbbProductItem — the size modal renders its price with the same class,
  // and it is built on open, which is why this runs off the observer rather than once.
  // The footer's totals use their own classes and are deliberately untouched.
  for (const price of document.querySelectorAll(".gbbProductPrice")) {
    if (price.dataset[PRICE_ATTR]) continue;
    const next = dropWholeCents(price.textContent);
    price.dataset[PRICE_ATTR] = "1";
    if (next !== price.textContent) price.textContent = next;
  }

  markProductsInBox(cards);
  // After the cents trim above, so both read the same rendered text.
  orderByPriceDescending(cards);

  for (const card of cards) {
    const el = card.querySelector(".gbbProductTitleOnly");
    if (!el || el.dataset[DONE_ATTR]) continue;

    const product = products().get(card.dataset.productId);
    if (!product) continue;
    const parts = splitTitle(el.textContent, product.vendor, product.type);
    if (!parts) continue;

    // Flagged BEFORE the write, so the mutation this causes finds nothing to do and the
    // observer settles instead of re-entering.
    el.dataset[DONE_ATTR] = "1";
    const name = document.createElement("span");
    name.className = NAME_CLASS;
    name.textContent = parts.name;
    const meta = document.createElement("span");
    meta.className = META_CLASS;
    meta.textContent = parts.meta;
    el.replaceChildren(name, meta);
  }
}

function start() {
  installSizeGuard();
  let queued = false;
  // setTimeout rather than requestAnimationFrame: rAF does not fire while a tab is
  // unpainted, so a page opened in a background tab — or graded in a hidden browser, which
  // is how this was caught — would sit with every title unsplit until it was focused.
  // Nothing here draws, so there is no reason to wait for paint.
  const schedule = () => {
    if (queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      apply();
    }, 0);
  };
  schedule();
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    // The size row marks its selection by flipping this attribute alone, which no childList
    // record reports.
    attributes: true,
    attributeFilter: ["aria-checked"],
  });
}

// Guarded so test/cardTitle.test.ts can import the pure helpers without a DOM.
if (typeof document !== "undefined") {
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
